import React, { useState, useEffect } from "react";
import { 
  Search, 
  Filter, 
  Calendar, 
  ChevronLeft, 
  ChevronRight,
  TrendingUp,
  Download,
  Eye,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";
import { apiClient } from "@/config/api.config";
import { toast } from "react-hot-toast";

const AsperoBondInvestments = () => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ page: 1, total: 0, pages: 1 });
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: "",
    isin: "",
    search: ""
  });
  
  // Details Modal State
  const [selectedInv, setSelectedInv] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [syncLoading, setSyncLoading] = useState(false);

  const fetchInvestments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.status) params.append("status", filters.status);
      if (filters.isin) params.append("isin", filters.isin);
      
      const response = await apiClient.get(`/admin/aspero/bond-investments?${params.toString()}`);
      
      if (response.data?.success) {
        setInvestments(response.data.data || []);
        setMeta(response.data.meta || { page: 1, total: 0, pages: 1 });
      }
    } catch (error) {
      console.error("Error fetching bond investments:", error);
      toast.error("Failed to load bond investments");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncHistory = async () => {
    try {
      setSyncLoading(true);
      const response = await apiClient.post("/admin/aspero/sync-history");
      if (response.data?.success) {
        const { synced, total, enriched } = response.data.data;
        toast.success(`Successfully synced ${synced} of ${total} logs! Enriched ${enriched} records.`);
        fetchInvestments();
      }
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Failed to sync historic bond orders");
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestments();
  }, [filters.page, filters.status, filters.isin]);

  const handleStatusChange = (e) => {
    setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= meta.pages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const openDetails = (inv) => {
    setSelectedInv(inv);
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PAYMENT_SUCCESS':
      case 'PAYMENT_SUCCESSFUL':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" /> Success
          </span>
        );
      case 'PAYMENT_FAILED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" /> Failed
          </span>
        );
      case 'PAYMENT_PENDING':
      case 'PAYMENT_INITIATED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" /> Pending
          </span>
        );
      case 'CONFIRMED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <AlertCircle className="w-3 h-3 mr-1" /> Initial
          </span>
        );
      case 'REPORTED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            <TrendingUp className="w-3 h-3 mr-1" /> Reported
          </span>
        );
      case 'SETTLED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            <CheckCircle className="w-3 h-3 mr-1" /> Settled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="text-blue-600" />
            Bond Investment Orders
          </h1>
          <p className="text-sm text-gray-600">
            Manage all bond investments and transactions across the platform
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={handleSyncHistory}
            disabled={syncLoading}
            className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm ${syncLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {syncLoading ? (
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Syncing...
              </div>
            ) : (
              <>
                <TrendingUp className="w-4 h-4 mr-2" />
                Sync Historic Orders
              </>
            )}
          </button>
          
          <button 
            onClick={fetchInvestments}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Refresh Data
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search ISIN or Order ID..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 outline-none"
              value={filters.isin}
              onChange={(e) => setFilters(prev => ({ ...prev, isin: e.target.value }))}
              onKeyPress={(e) => e.key === 'Enter' && fetchInvestments()}
            />
          </div>
          
          <div>
            <select
              value={filters.status}
              onChange={handleStatusChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 outline-none"
            >
              <option value="">All Statuses</option>
              <option value="PAYMENT_SUCCESS">Payment Success</option>
              <option value="REPORTED">Reported</option>
              <option value="SETTLED">Settled</option>
              <option value="PAYMENT_PENDING">Pending Payment</option>
              <option value="PAYMENT_FAILED">Payment Failed</option>
              <option value="CONFIRMED">Confirmation Sent</option>
            </select>
          </div>
          
          <div className="md:col-span-2 flex justify-end">
             <div className="text-sm text-gray-500 self-center">
               Showing {investments.length} of {meta.total} orders
             </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Order / Date</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">User Details</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Bond Asset</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Financials</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Preview</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="6" className="px-6 py-4 h-16 bg-gray-50/50"></td>
                  </tr>
                ))
              ) : investments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No bond investment orders found matching your filters.
                  </td>
                </tr>
              ) : (
                investments.map((inv) => (
                  <tr key={inv._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900 group flex items-center">
                         #{inv.orderId || 'N/A'}
                         {inv.category && <span className="ml-2 text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded uppercase">{inv.category}</span>}
                      </div>
                      <div className="text-[11px] text-gray-500 flex items-center mt-1">
                        <Calendar className="w-3 h-3 mr-1" />
                        {inv.investedAt ? new Date(inv.investedAt).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{inv.userId ? (inv.userId.fullName || 'Registered User') : 'Unknown User'}</div>
                      <div className="text-xs text-gray-500">{inv.userId?.email || 'No Email'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-blue-700 truncate max-w-[200px]" title={inv.instrumentName}>
                        {inv.instrumentName || 'Fetching Name...'}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                         <span className="font-mono bg-gray-100 px-1 rounded">{inv.isin || 'N/A'}</span>
                         {inv.rating && <span className="font-bold text-emerald-600 border border-emerald-200 px-1 rounded bg-emerald-50 text-[10px]">{inv.rating}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">{formatCurrency(inv.amount)}</div>
                      <div className="flex items-center gap-2 mt-1">
                         {inv.ytm && <span className="text-[10px] text-gray-600">Yield: <b className="text-gray-900">{inv.ytm}%</b></span>}
                         {inv.couponRate && <span className="text-[10px] text-gray-600">Coupon: <b className="text-gray-900">{inv.couponRate}%</b></span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(inv.status)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => openDetails(inv)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta.pages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {meta.page} of {meta.pages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(meta.page - 1)}
                disabled={meta.page === 1}
                className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(meta.page + 1)}
                disabled={meta.page === meta.pages}
                className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showModal && selectedInv && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-200">
             {/* Modal Header */}
             <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
               <div>
                 <h2 className="text-xl font-bold text-gray-900">Order Details</h2>
                 <p className="text-xs text-gray-500">System Log ID: {selectedInv._id}</p>
               </div>
               <button 
                 onClick={() => setShowModal(false)}
                 className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-full transition-all"
               >
                 <XCircle className="w-6 h-6" />
               </button>
             </div>

             {/* Modal Body */}
             <div className="p-6 overflow-y-auto space-y-6">
                {/* Asset Header */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 rounded-xl text-white shadow-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold">{selectedInv.instrumentName || 'Aspero Bond Asset'}</h3>
                      <p className="text-blue-100 text-sm font-mono mt-1">ISIN: {selectedInv.isin}</p>
                    </div>
                    {getStatusBadge(selectedInv.status)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm">
                      <div className="text-[10px] text-blue-200 uppercase font-bold">Amount</div>
                      <div className="text-lg font-bold">{formatCurrency(selectedInv.amount)}</div>
                    </div>
                    <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm">
                      <div className="text-[10px] text-blue-200 uppercase font-bold">Units</div>
                      <div className="text-lg font-bold">{selectedInv.units}</div>
                    </div>
                    <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm">
                      <div className="text-[10px] text-blue-200 uppercase font-bold">Yield (YTM)</div>
                      <div className="text-lg font-bold">{selectedInv.ytm || '--'}%</div>
                    </div>
                    <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm">
                      <div className="text-[10px] text-blue-200 uppercase font-bold">Rating</div>
                      <div className="text-lg font-bold">{selectedInv.rating || '--'}</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Transaction Info */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" /> Transaction Metadata
                    </h4>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-100">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Aspero Order ID:</span>
                        <span className="text-xs font-bold text-gray-900">{selectedInv.orderId || 'Not Generated'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Confirmation Date:</span>
                        <span className="text-xs font-bold text-gray-900">
                          {selectedInv.investedAt ? new Date(selectedInv.investedAt).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Maturity Date:</span>
                        <span className="text-xs font-bold text-gray-900">
                          {selectedInv.maturityDate ? new Date(selectedInv.maturityDate).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-gray-200 pt-2">
                         <span className="text-xs text-gray-500">Tenor:</span>
                         <span className="text-xs font-bold text-gray-900">{selectedInv.tenor || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* User Info */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                       <TrendingUp className="w-4 h-4 text-blue-600" /> Professional Insight
                    </h4>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-100">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Asset Category:</span>
                        <span className="text-xs font-bold text-gray-900 uppercase">{selectedInv.category || 'NCD'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Asset Sector:</span>
                        <span className="text-xs font-bold text-gray-900">{selectedInv.sector || 'Financial'}</span>
                      </div>
                      <div className="flex justify-between border-t border-gray-200 pt-2">
                        <span className="text-xs text-gray-500">Investor:</span>
                        <span className="text-xs font-bold text-gray-900">{selectedInv.userId?.fullName || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Investor Email:</span>
                        <span className="text-xs font-bold text-blue-600">{selectedInv.userId?.email || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Raw Event Data (Accordion-style) */}
                <div className="space-y-2">
                   <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Raw Webhook Audit Log</h4>
                   <pre className="text-[10px] bg-gray-900 text-emerald-400 p-4 rounded-xl overflow-x-auto font-mono opacity-80 border border-gray-800">
                     {JSON.stringify(selectedInv.rawEventData, null, 2)}
                   </pre>
                </div>
             </div>

             {/* Modal Footer */}
             <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                <button 
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-black transition-all shadow-lg"
                >
                  Close Record
                </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AsperoBondInvestments;
